import {
  Component,
  Input,
  Output,
  EventEmitter,
  ContentChild,
  ViewChild,
  ElementRef,
  Inject,
  OnDestroy,
  AfterViewInit,
} from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import { ModalComponent } from '../modal/modal.component';
import {
  ModalLayoutTitleDirective,
  ModalLayoutSubTitleDirective,
  ModalLayoutButtonsDirective,
} from './modal-layout-extensions';
import { DOCUMENT } from '@angular/common';
import { Subject } from 'rxjs';

@Component({
  selector: 'cigna-modal-layout',
  templateUrl: './modal-layout.component.html',
  styleUrls: ['./modal-layout.component.scss'],
})
export class ModalLayoutComponent implements AfterViewInit, OnDestroy {
  private _onDestroy$ = new Subject<void>();
  private _previousFocusedElement: (Element & { focus?(): void }) | null;

  /**
   * Set the title for the modal.
   */
  @Input() title: string;
  /**
   * Set the subtitle for the modal.
   */
  @Input() subTitle: string;
  /**
   * Determine if there is a **primary** button in the modal footer. If a non-empty string
   * is provided, the button will display and this will be the text of the button.
   */
  @Input() ok: string;
  /**
   * Determine if there is a **secondary** button in the modal footer. If a non-empty string
   * is provided, the button will display and this will be the text of the button.
   */
  @Input() alt: string;
  /**
   * Determine if there is a close button (X) for the modal and what text appears next to the
   * X. If this value is `null`, do not show the close button. If this value is a non-empty
   * string, show a close button with the given text.
   */
  @Input() close?: string | null;
  /**
   * Emits when the primary button is clicked
   */
  @Output() okClicked = new EventEmitter();
  /**
   * Emits when the secondary button is clicked.
   */
  @Output() altClicked = new EventEmitter<void>();
  /**
   * Emits when the close button is clicked.
   */
  @Output() closeClicked = new EventEmitter();

  /** @internal */
  @ContentChild(ModalLayoutButtonsDirective, { static: false })
  _buttonsTemplate?: ModalLayoutButtonsDirective;

  /** @internal */
  @ContentChild(ModalLayoutTitleDirective, { static: false })
  titleTemplate?: ModalLayoutTitleDirective;

  /** @internal */
  @ContentChild(ModalLayoutSubTitleDirective, { static: false })
  subTitleTemplate?: ModalLayoutSubTitleDirective;

  /** @internal */
  @ViewChild('modalContent', { read: ElementRef, static: false })
  modalContent: ElementRef<HTMLElement>;

  get buttonsTemplate() {
    return this._buttonsTemplate && this._buttonsTemplate.templateRef;
  }

  get hasTitle() {
    return !!(
      this.title ||
      this.titleTemplate ||
      this.subTitle ||
      this.subTitleTemplate
    );
  }

  get hasControls() {
    return !!(this.ok || this.alt || this._buttonsTemplate);
  }

  constructor(
    public modal: ModalComponent,
    @Inject(DOCUMENT) private _document: Document,
  ) {
    modal.opened
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(() => this.focusModalContent());

    modal.closed.pipe(takeUntil(this._onDestroy$)).subscribe(() => {
      if (this._previousFocusedElement && this._previousFocusedElement.focus) {
        this._previousFocusedElement.focus();
      }
    });
  }

  /** @internal */
  ngAfterViewInit() {
    // modal layout rendering may be delayed (async)
    this.focusModalContent();
  }

  /** @internal */
  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  okClick(event?: Event) {
    this.okClicked.next(event);
    this.modal.close(event);
  }

  altClick() {
    this.altClicked.next();
  }

  closeClick(event?: Event) {
    this.modal.close(event);
    this.closeClicked.next(event);
  }

  private focusModalContent() {
    if (
      this.modal.isOpened &&
      this.modalContent &&
      this.modalContent.nativeElement !== this._document.activeElement
    ) {
      this._previousFocusedElement = this._document.activeElement;

      this.modalContent.nativeElement.focus();
    }
  }
}
